/**
 * @fileoverview Dados da área de Configurações. Carrega as configurações da
 * empresa e a própria empresa uma vez só (trocar de seção não busca de novo) e
 * grava cada chave pelo PUT /settings/:key de sempre: é ele que avisa por socket
 * o menu, a lista de conversas e as outras telas que leem essas chaves.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useSettings from "../../hooks/useSettings";
import { i18n } from "../../translate/i18n";

const isNewer = (row, current) => {
  const diff = new Date(row.updatedAt) - new Date(current.updatedAt);
  return diff > 0 || (diff === 0 && row.id > current.id);
};

/**
 * Valor de cada chave. A tabela aceita linhas repetidas da mesma chave; vale a
 * mais nova, como no backend (SettingsCache: updatedAt DESC, id DESC). As abas
 * antigas pegavam a primeira que aparecia.
 */
export const settingsByKey = (rows) => {
  const newest = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.key) return;
    if (!newest[row.key] || isNewer(row, newest[row.key])) newest[row.key] = row;
  });
  return Object.fromEntries(Object.entries(newest).map(([key, row]) => [key, row.value]));
};

const showSaveError = (err) => {
  if (err?.response?.data?.error) toastError(err);
  else toast.error(i18n.t("settings.area.saveError"));
};

/**
 * `needsSettings` e `needsCompany` dizem o que buscar; enquanto `enabled` for
 * falso (usuário ainda não carregado), nada é buscado.
 */
export default function useSettingsArea({ enabled, needsSettings, needsCompany, companyId }) {
  const { update } = useSettings();
  const [values, setValues] = useState({});
  const [company, setCompany] = useState(null);
  const [status, setStatus] = useState("loading");
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  // Último valor que o servidor tem de cada chave (carregado ou gravado com
  // sucesso): é para ele que a tela volta quando uma gravação falha.
  const confirmed = useRef({});
  const lastCall = useRef({});

  const load = useCallback(async () => {
    if (!enabled) return;
    setStatus("loading");
    try {
      // Sem empresa na sessão não dá para mostrar nem gravar horários e a política
      // de 2FA (o PUT iria para /companies/undefined): vira erro com "Tentar de novo".
      if (needsCompany && !companyId) throw new Error("Usuário sem empresa na sessão");
      const [rows, companyData] = await Promise.all([
        needsSettings ? api.get("/settings").then(({ data }) => data) : [],
        needsCompany ? api.get(`/companies/${companyId}`).then(({ data }) => data) : null,
      ]);
      const loaded = settingsByKey(rows);
      confirmed.current = { ...loaded };
      setValues(loaded);
      setCompany(companyData);
      setStatus("ready");
    } catch (err) {
      toastError(err);
      setStatus("error");
    }
  }, [enabled, needsSettings, needsCompany, companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const get = useCallback(
    (key, fallback) => (values[key] === undefined || values[key] === null ? fallback : values[key]),
    [values]
  );

  // Atualiza na hora e, se o servidor recusar, volta ao último valor que ele
  // aceitou (antes ficava o valor novo na tela, errado, até recarregar). Só a
  // última gravação de cada chave decide o que fica na tela.
  const save = useCallback(
    async (key, value) => {
      const call = (lastCall.current[key] || 0) + 1;
      lastCall.current[key] = call;
      setValues((current) => ({ ...current, [key]: value }));
      setSaving((current) => ({ ...current, [key]: true }));
      try {
        await update({ key, value });
        confirmed.current[key] = value;
        if (lastCall.current[key] === call) setSaved((current) => ({ ...current, [key]: Date.now() }));
        return true;
      } catch (err) {
        if (lastCall.current[key] === call) {
          setValues((current) => ({ ...current, [key]: confirmed.current[key] }));
        }
        showSaveError(err);
        return false;
      } finally {
        if (lastCall.current[key] === call) setSaving((current) => ({ ...current, [key]: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const updateCompany = useCallback((changes) => {
    setCompany((current) => (current ? { ...current, ...changes } : current));
  }, []);

  return { status, values, get, save, saving, saved, company, updateCompany, reload: load };
}

/** Props de um SettingItem ligado a uma chave (valor, gravação e o retorno visual). */
export const bindSetting = (area, key, fallback) => ({
  id: key,
  value: area.get(key, fallback),
  onChange: (value) => area.save(key, value),
  loading: !!area.saving[key],
  saved: area.saved[key],
});

/**
 * Campo de texto que se salva sozinho: grava 1 s depois de parar de digitar e
 * na hora ao sair do campo ou da seção; não grava o que já está salvo.
 * `toValue` converte o texto da tela no valor gravado; `fromValue`, o contrário.
 * `valid`, se vier, segura a gravação enquanto o texto não passa.
 */
export function useDraftSetting(
  area,
  key,
  { fallback = "", toValue = (text) => text, fromValue = (value) => value, valid = () => true } = {}
) {
  const [text, setText] = useState(() => fromValue(area.get(key, fallback)));
  const lastSaved = useRef(null);
  if (lastSaved.current === null) lastSaved.current = toValue(text);
  const textRef = useRef(text);
  textRef.current = text;

  const commit = useDebouncedCallback((next) => {
    if (!valid(next)) return;
    const value = toValue(next);
    if (value === lastSaved.current) return;
    // Só conta como salvo se o servidor aceitou: depois de um erro, sair do
    // campo tenta de novo.
    return area.save(key, value).then((ok) => {
      if (ok) lastSaved.current = value;
    });
  }, 1000);

  // Sair da seção no meio da digitação também grava.
  useEffect(() => () => commit.flush(), [commit]);

  // Troca o texto como se a pessoa tivesse digitado (ex.: "Restaurar padrão").
  const change = (next) => {
    setText(next);
    commit(next);
  };

  return {
    value: text,
    setValue: setText,
    change,
    onChange: (event) => change(event.target.value),
    // Ao sair do campo: grava agora o que estiver diferente do salvo (a
    // promessa termina quando o servidor responder).
    flush: () => {
      commit(textRef.current);
      return Promise.resolve(commit.flush());
    },
    saving: !!area.saving[key],
    saved: area.saved[key],
  };
}
